import * as React from 'react';
import { cn, getTestId } from '../utils';

type BaseInputAttributes = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size' | 'onChange'
>;

export interface CheckboxProps extends BaseInputAttributes {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  label?: string;
  description?: string;
  className?: string;
  onChange?: (checked: boolean) => void;
  testId?: string;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      defaultChecked,
      onChange,
      label,
      description,
      disabled,
      className,
      testId,
      id,
      ...props
    },
    ref
  ) => {
    const inputEl = (
      <input
        ref={ref}
        type='checkbox'
        id={id}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        data-testid={getTestId({ testId, id })}
        onChange={e => onChange?.(e.target.checked)}
        className={cn(
          'w-4 h-4 accent-primary-base cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      />
    );

    if (!label) return inputEl;

    return (
      <div className='flex flex-row gap-sm items-center'>
        {inputEl}
        <label
          htmlFor={id}
          className={cn(
            'flex flex-col flex-nowrap',
            disabled && 'opacity-50',
            id && 'cursor-pointer'
          )}
        >
          <span className='text-md font-medium'>{label}</span>
          {description && (
            <span className='font-normal text-sm text-foreground-muted'>{description}</span>
          )}
        </label>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
